import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDraggable,
  useDroppable,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import LossReasonModal from './LossReasonModal';
import AgentFilter from './AgentFilter';

// Stage order — matches the Home page pipeline bar colors.
const STAGES = [
  { key: 'identified',     label: 'Identified',     color: '#546E7A' },
  { key: 'touring',        label: 'Touring',        color: '#00D4FF' },
  { key: 'negotiating',    label: 'Negotiating',    color: '#FFC107' },
  { key: 'loi',            label: 'LOI',            color: '#FF6B35' },
  { key: 'under_contract', label: 'Under Contract', color: '#00E676' },
  { key: 'closed',         label: 'Closed',         color: '#4CAF50' },
  { key: 'lost',           label: 'Lost',           color: '#EF4444' },
];
const STAGE_KEYS = STAGES.map((s) => s.key);

function fitColor(pct) {
  if (pct == null) return '#9CA3AF';
  if (pct >= 75) return '#00E676';
  if (pct >= 60) return '#00D4FF';
  if (pct >= 45) return '#FDE68A';
  if (pct >= 30) return '#FDBA74';
  return '#FCA5A5';
}

function listingBadge(listingType) {
  if (!listingType) return null;
  if (listingType === 'for_lease') return { label: 'Lease', cls: 'bg-[#00D4FF]/15 text-[#00D4FF] border-[#00D4FF]/40' };
  if (listingType === 'for_sale')  return { label: 'Sale',  cls: 'bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/40' };
  if (listingType === 'for_sale_and_lease') return { label: 'Sale/Lease', cls: 'bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40' };
  return { label: listingType, cls: 'bg-gray-500/20 text-gray-300 border-gray-500/40' };
}

function formatPrice(d) {
  if (d.listing_type === 'for_lease' && d.lease_rate_sqft) {
    return `$${d.lease_rate_sqft.toFixed(2)}/sqft`;
  }
  if (d.price) return `$${Number(d.price).toLocaleString()}`;
  if (d.lease_rate_sqft) return `$${d.lease_rate_sqft.toFixed(2)}/sqft`;
  return '';
}

function dealKey(d) { return `${d.property_id}-${d.buyer_id}`; }

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

// Pure card visual — used both for the draggable card and the DragOverlay copy.
function DealCardContent({ deal, dragHandleProps, recentlyMoved, onEditLoss }) {
  const navigate = useNavigate();
  const lt = listingBadge(deal.listing_type);
  const fit = fitColor(deal.fit_pct);
  const priceLine = formatPrice(deal);
  const isLost = deal.stage === 'lost';

  return (
    <div
      onClick={(e) => {
        if (e.target.closest('[data-dnd-handle]') || e.target.closest('[data-loss-action]')) return;
        navigate(`/properties/${deal.property_id}/buyer/${deal.buyer_id}`);
      }}
      className={`relative rounded-lg border bg-[#0A0E27]/60 hover:bg-[#243B55]/40 transition-all cursor-pointer
        ${recentlyMoved ? 'border-[#00E676] shadow-[0_0_0_2px_rgba(0,230,118,0.4)]' : 'border-[#546E7A]/20'}`}
    >
      <div className="flex items-stretch">
        {/* Drag handle — only this strip starts a drag, so the rest of the card stays scrollable / clickable */}
        <button
          data-dnd-handle
          {...(dragHandleProps || {})}
          className="px-2 flex items-center text-gray-500 hover:text-white touch-none cursor-grab active:cursor-grabbing rounded-l-lg"
          aria-label="Drag deal"
          onClick={(e) => e.stopPropagation()}
        >
          ⠿
        </button>
        <div className="flex-1 min-w-0 p-3 pl-1">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-white font-semibold text-sm truncate flex-1">
              {deal.property_address}
            </div>
            <span className="text-sm font-bold tabular-nums shrink-0" style={{ color: fit }}>
              {deal.fit_pct != null ? `${deal.fit_pct}%` : '—'}
            </span>
          </div>
          {(deal.property_city || deal.property_state) && (
            <div className="text-gray-500 text-xs truncate">
              {[deal.property_city, deal.property_state].filter(Boolean).join(', ')}
            </div>
          )}
          <div className="text-gray-400 text-xs mt-1.5 truncate">{deal.buyer_name}</div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {lt && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${lt.cls}`}>
                {lt.label}
              </span>
            )}
            {priceLine && <span className="text-gray-300 text-xs">{priceLine}</span>}
            <span className="text-gray-500 text-xs ml-auto">
              {deal.days_in_stage != null
                ? `${deal.days_in_stage} day${deal.days_in_stage === 1 ? '' : 's'}`
                : ''}
            </span>
          </div>

          {/* Loss-specific footer — only renders for the 'lost' stage */}
          {isLost && (
            <div className="mt-2 pt-2 border-t border-red-500/20 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                {deal.loss_reason && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border bg-red-500/15 text-red-300 border-red-500/40 uppercase tracking-wide">
                    {(deal.loss_reason || '').replace('_', ' ')}
                  </span>
                )}
                {onEditLoss && (
                  <button
                    data-loss-action
                    onClick={(e) => { e.stopPropagation(); onEditLoss(deal); }}
                    className="ml-auto text-[10px] text-[#00D4FF] hover:text-[#00E676] font-medium"
                  >
                    {deal.loss_reason ? 'View/Edit' : 'Add details'}
                  </button>
                )}
              </div>
              {deal.loss_notes && (
                <div className="text-gray-400 text-[11px] italic line-clamp-2">
                  "{truncate(deal.loss_notes, 50)}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DraggableDealCard({ deal, recentlyMoved, onEditLoss }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dealKey(deal),
    data: { deal },
  });
  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.35 : 1 }}>
      <DealCardContent
        deal={deal}
        recentlyMoved={recentlyMoved}
        dragHandleProps={{ ...attributes, ...listeners }}
        onEditLoss={onEditLoss}
      />
    </div>
  );
}

// Each stage section is the droppable target. The grid of cards inside drops
// onto the whole section.
function StageSection({ stage, deals, recentlyMoved, onEditLoss }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage-${stage.key}`,
    data: { stageKey: stage.key },
  });

  return (
    <section
      ref={setNodeRef}
      className={`bg-[#1A2744] rounded-xl border p-4 md:p-5 transition-colors
        ${isOver ? 'border-[#00D4FF] bg-[#1A2744]/95 ring-2 ring-[#00D4FF]/40' : 'border-[#546E7A]/30'}`}
      style={{ borderLeft: `4px solid ${stage.color}` }}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }}></span>
          {stage.label}
        </h2>
        <span className="text-gray-400 text-sm tabular-nums">
          {deals.length} deal{deals.length === 1 ? '' : 's'}
        </span>
      </div>

      {deals.length === 0 ? (
        <div className={`rounded-lg border-2 border-dashed py-6 text-center text-xs italic transition-colors
          ${isOver ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-[#546E7A]/30 text-gray-500'}`}>
          {isOver ? 'Release to drop here' : 'No deals in this stage'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {deals.map((d) => (
            <DraggableDealCard
              key={dealKey(d)}
              deal={d}
              recentlyMoved={recentlyMoved === dealKey(d)}
              onEditLoss={onEditLoss}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function DealPipeline() {
  const { user } = useAuth();
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDeal, setActiveDeal] = useState(null);
  const [recentlyMoved, setRecentlyMoved] = useState(null);
  const [toast, setToast] = useState('');
  // When a stage change targets 'lost', we capture the pending deal here and
  // wait for the modal to confirm/cancel before sending the PATCH.
  const [pendingLoss, setPendingLoss] = useState(null);
  // Broker drill-down: null = brokerage-wide. Agents ignore this entirely on the backend.
  const [filterAgentId, setFilterAgentId] = useState(null);
  // Stage filter — when set, only that stage section renders. Driven by both
  // the in-page dropdown and the ?stage= URL param so links from the Broker
  // Dashboard pipeline bar deep-link directly to a focused view.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStage = searchParams.get('stage');
  const [filterStage, setFilterStage] = useState(urlStage || 'all');
  // Refs per stage section — used to scroll the deep-linked stage into view.
  const sectionRefs = useRef({});
  // When a stage is opened from a URL param (e.g. clicked on Broker Dashboard),
  // give it a brief glow so the broker can visually confirm the landing.
  const [highlightStage, setHighlightStage] = useState(null);

  // Both sensors armed — desktop click-vs-drag separation, mobile delay so a
  // tap on the card still navigates and a vertical scroll isn't hijacked.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const loadPipeline = useCallback(async () => {
    try {
      const qs = filterAgentId != null ? `?agent_id=${filterAgentId}` : '';
      const res = await authFetch(`${API}/api/deals/pipeline${qs}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to load (${res.status})`);
      }
      setPipeline(await res.json());
    } catch (e) {
      setError(e.message || 'Could not load pipeline.');
    } finally {
      setLoading(false);
    }
  }, [filterAgentId]);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);

  // Keep state in sync with the ?stage= URL param (e.g., browser back/forward).
  useEffect(() => {
    setFilterStage(urlStage || 'all');
  }, [urlStage]);

  // After data loads, scroll the deep-linked stage section into view + flash
  // a brief highlight so the broker can confirm the landing.
  useEffect(() => {
    if (!pipeline || filterStage === 'all') return;
    const el = sectionRefs.current[filterStage];
    if (el) {
      const scrollT = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightStage(filterStage);
      }, 60);
      const clearT = setTimeout(() => setHighlightStage(null), 1800);
      return () => { clearTimeout(scrollT); clearTimeout(clearT); };
    }
  }, [pipeline, filterStage]);

  const handleStageFilterChange = (next) => {
    setFilterStage(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('stage');
    else params.set('stage', next);
    setSearchParams(params, { replace: true });
  };

  if (!user) return <Navigate to="/" replace />;
  if (!['super_admin', 'broker', 'agent'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Apply the optimistic local move + persist to the API.
  // Extra body fields (e.g. loss_reason/loss_notes) are merged in for the lost stage.
  const persistStage = async (deal, newStage, extra = {}) => {
    setPipeline((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      STAGE_KEYS.forEach((k) => {
        next[k] = (prev[k] || []).filter((d) => dealKey(d) !== dealKey(deal));
      });
      next[newStage] = [
        { ...deal, stage: newStage, stage_label: STAGES.find(s => s.key === newStage)?.label, days_in_stage: 0 },
        ...(next[newStage] || []),
      ];
      const totals = { ...(prev.totals || {}) };
      totals[deal.stage] = Math.max(0, (totals[deal.stage] || 0) - 1);
      totals[newStage] = (totals[newStage] || 0) + 1;
      next.totals = totals;
      return next;
    });
    setRecentlyMoved(dealKey(deal));
    setTimeout(() => setRecentlyMoved(null), 1200);

    try {
      const res = await authFetch(`${API}/api/deals/${deal.property_id}/buyer/${deal.buyer_id}/stage`, {
        method: 'PATCH',
        body: JSON.stringify({ stage: newStage, ...extra }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Update failed (${res.status})`);
      }
      setToast(`✓ Moved to ${STAGES.find(s => s.key === newStage)?.label}`);
      setTimeout(() => setToast(''), 2000);
      loadPipeline();
    } catch (e) {
      setToast(`⚠ ${e.message}`);
      setTimeout(() => setToast(''), 3000);
      loadPipeline();
      throw e;
    }
  };

  const updateStage = async (deal, newStage) => {
    if (!deal || deal.stage === newStage) return;
    // Intercept "lost" so the user can record why before we persist anything
    if (newStage === 'lost') {
      setPendingLoss({ deal, mode: 'record' });
      return;
    }
    await persistStage(deal, newStage);
  };

  // Open the modal in edit mode pre-populated with the deal's current loss details
  const editLoss = (deal) => {
    setPendingLoss({ deal, mode: 'edit' });
  };

  const handleDragStart = (event) => {
    const deal = event.active?.data?.current?.deal;
    if (deal) {
      setActiveDeal(deal);
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(50);
    }
  };

  const handleDragEnd = (event) => {
    setActiveDeal(null);
    const { active, over } = event;
    if (!over) return;
    const deal = active.data?.current?.deal;
    const overStage = over.data?.current?.stageKey;
    if (!deal || !overStage) return;
    if (deal.stage === overStage) return;
    updateStage(deal, overStage);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading pipeline...</div>
      </div>
    );
  }

  const totals = (pipeline && pipeline.totals) || {};
  const totalActive = STAGES.filter(s => s.key !== 'lost').reduce((sum, s) => sum + (totals[s.key] || 0), 0);
  const lostCount = totals.lost || 0;
  const clientSet = new Set();
  STAGES.forEach((s) => {
    if (s.key === 'lost') return;
    const list = (pipeline && pipeline[s.key]) || [];
    list.forEach((d) => clientSet.add(d.buyer_id));
  });
  const clientCount = clientSet.size;

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="mb-4">
          <Link to="/" className="text-[#00D4FF] text-sm hover:text-[#00E676]">← Back to Home</Link>
          <div className="flex items-start justify-between gap-3 flex-wrap mt-2">
            <h1 className="text-2xl md:text-3xl font-bold text-white">Deal Pipeline</h1>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-xs uppercase tracking-wider">Stage</label>
                <select
                  value={filterStage}
                  onChange={(e) => handleStageFilterChange(e.target.value)}
                  className="bg-[#0A0E27] border border-[#546E7A]/50 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]"
                >
                  <option value="all">All Stages</option>
                  {STAGES.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <AgentFilter value={filterAgentId} onChange={setFilterAgentId} />
            </div>
          </div>
          <p className="text-gray-400 text-sm mt-1">
            {totalActive} active deal{totalActive === 1 ? '' : 's'} across {clientCount} client{clientCount === 1 ? '' : 's'}
            {lostCount > 0 && <span className="text-gray-500"> · {lostCount} lost</span>}
            {filterStage !== 'all' && (
              <span className="text-[#00D4FF]"> · filtered to {STAGES.find(s => s.key === filterStage)?.label}</span>
            )}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Drag deals between stages using the ⠿ handle. Tap a card to open it.
          </p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-500/40 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        {toast && (
          <div className="fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium bg-[#1A2744] border border-[#00E676]/40 text-white">
            {toast}
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDeal(null)}
        >
          <div className="space-y-4">
            {STAGES.map((stage) => {
              const deals = (pipeline && pipeline[stage.key]) || [];
              // Hide Lost only when it's empty AND nothing is being dragged
              if (stage.key === 'lost' && deals.length === 0 && !activeDeal) return null;
              // Stage filter — when a specific stage is selected, hide the others
              if (filterStage !== 'all' && filterStage !== stage.key) return null;
              const isHighlighted = highlightStage === stage.key;
              return (
                <div
                  key={stage.key}
                  ref={(el) => { sectionRefs.current[stage.key] = el; }}
                  className={`rounded-xl transition-shadow duration-700 ${isHighlighted ? 'ring-2 ring-[#00D4FF] shadow-[0_0_30px_rgba(0,212,255,0.4)]' : ''}`}
                >
                  <StageSection
                    stage={stage}
                    deals={deals}
                    recentlyMoved={recentlyMoved}
                    onEditLoss={editLoss}
                  />
                </div>
              );
            })}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeDeal ? (
              <div className="w-[280px] scale-[1.03] shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
                <DealCardContent deal={activeDeal} recentlyMoved={false} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <LossReasonModal
        open={!!pendingLoss}
        deal={pendingLoss?.deal}
        mode={pendingLoss?.mode === 'edit' ? 'edit' : 'record'}
        initialReason={pendingLoss?.mode === 'edit' ? (pendingLoss?.deal?.loss_reason || '') : ''}
        initialNotes={pendingLoss?.mode === 'edit' ? (pendingLoss?.deal?.loss_notes || '') : ''}
        onCancel={() => setPendingLoss(null)}
        onConfirm={async ({ reason, notes }) => {
          const { deal } = pendingLoss;
          try {
            // Both record + edit hit the same PATCH with stage='lost'; the backend
            // upserts loss_reason / loss_notes either way.
            await persistStage(deal, 'lost', { loss_reason: reason, loss_notes: notes });
            setPendingLoss(null);
          } catch (e) {
            throw e;
          }
        }}
      />
    </div>
  );
}
